import React, { useState, useEffect } from 'react';
import { AlertCircle, Download, ChevronLeft, ChevronRight, FileWarning } from 'lucide-react';
import { ImportError } from '@/types/import';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface ImportErrorTableProps {
  jobId: string;
  totalErrors: number;
  getJobErrors: (jobId: string, page: number) => Promise<{ errors: ImportError[]; total: number }>;
}

export const ImportErrorTable: React.FC<ImportErrorTableProps> = ({
  jobId,
  totalErrors,
  getJobErrors,
}) => {
  const [errors, setErrors] = useState<ImportError[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(totalErrors);
  const [isLoading, setIsLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const pageSize = 10;
  const totalPages = Math.ceil(total / pageSize);

  useEffect(() => {
    const fetchErrors = async () => {
      setIsLoading(true);
      setFetchError(null);

      try {
        const result = await getJobErrors(jobId, page);
        setErrors(result.errors);
        setTotal(result.total);
      } catch (err) {
        setFetchError(err instanceof Error ? err.message : 'Failed to fetch errors');
      } finally {
        setIsLoading(false);
      }
    };

    if (totalErrors > 0) {
      fetchErrors();
    }
  }, [jobId, page, totalErrors, getJobErrors]);

  const handleDownloadErrors = () => {
    // Create CSV content
    const headers = ['Row Number', 'Field', 'Error Type', 'Message', 'Raw Data'];
    const rows = errors.map(error => [
      error.rowNumber,
      error.fieldName || '',
      error.errorType,
      error.message,
      error.rawData ? JSON.stringify(error.rawData) : '',
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')),
    ].join('\n');

    // Download file
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `import-errors-${jobId}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (totalErrors === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <FileWarning className="w-12 h-12 mx-auto text-gray-300 mb-3" />
        <p>No errors to display</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2 text-red-600">
          <AlertCircle className="w-5 h-5" />
          <span className="font-medium">{total} errors found</span>
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={handleDownloadErrors}
          className="flex items-center space-x-2"
        >
          <Download className="w-4 h-4" />
          <span>Download Errors CSV</span>
        </Button>
      </div>

      {/* Error message */}
      {fetchError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-600">
          {fetchError}
        </div>
      )}

      {/* Table */}
      <div className="border rounded-lg overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-b">
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                Row #
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                Field
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                Error Type
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Message
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {isLoading ? (
              <tr>
                <td colSpan={4} className="px-4 py-8 text-center text-gray-500">
                  <div className="flex items-center justify-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
                    <span>Loading errors...</span>
                  </div>
                </td>
              </tr>
            ) : errors.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-4 py-8 text-center text-gray-500">
                  No errors on this page
                </td>
              </tr>
            ) : (
              errors.map((error, index) => (
                <tr key={error.id || index} className={cn(
                  'hover:bg-gray-50',
                  index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'
                )}>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">
                    {error.rowNumber}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {error.fieldName || '-'}
                  </td>
                  <td className="px-4 py-3">
                    <span className={cn(
                      'inline-flex px-2 py-1 text-xs font-medium rounded-full',
                      error.errorType === 'VALIDATION' ? 'bg-yellow-100 text-yellow-800' :
                      error.errorType === 'REQUIRED' ? 'bg-red-100 text-red-800' :
                      error.errorType === 'FORMAT' ? 'bg-orange-100 text-orange-800' :
                      'bg-gray-100 text-gray-800'
                    )}>
                      {error.errorType}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {error.message}
                    {error.rawData && (
                      <details className="mt-1">
                        <summary className="text-xs text-blue-600 cursor-pointer hover:text-blue-700">
                          View raw data
                        </summary>
                        <pre className="mt-1 text-xs bg-gray-100 p-2 rounded overflow-x-auto">
                          {JSON.stringify(error.rawData, null, 2)}
                        </pre>
                      </details>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Showing {((page - 1) * pageSize) + 1} to {Math.min(page * pageSize, total)} of {total} errors
          </p>

          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1 || isLoading}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>

            <span className="text-sm text-gray-600">
              Page {page} of {totalPages}
            </span>

            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages || isLoading}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
